import React, { useState, useMemo } from 'react';
import { useParent } from '../context/ParentContext';
import ParentEmptyState from '../components/states/ParentEmptyState';
import './ParentAssignments.css';

function ParentAssignments() {
  const { currentAssignments, currentCourses, demoState } = useParent();
  const [filterCourse, setFilterCourse] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const filtered = useMemo(() => {
    let result = [...currentAssignments];
    if (filterCourse !== 'all') result = result.filter(a => a.courseId === filterCourse);
    if (filterStatus !== 'all') result = result.filter(a => a.status === filterStatus);
    if (filterPriority !== 'all') result = result.filter(a => a.priority === filterPriority);
    return result.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  }, [currentAssignments, filterCourse, filterStatus, filterPriority]);

  if (demoState === 'empty' || currentAssignments.length === 0) {
    return (
      <div className="parent-assignments">
        <h1 className="parent-page-title">Assignments</h1>
        <ParentEmptyState variant="no-assignments" />
      </div>
    );
  }

  return (
    <div className="parent-assignments">
      <h1 className="parent-page-title">Assignments</h1>

      {/* Filters */}
      <div className="parent-assignments__filters">
        <select
          value={filterCourse}
          onChange={e => setFilterCourse(e.target.value)}
          className="parent-filter-select"
          aria-label="Filter by course"
        >
          <option value="all">All Courses</option>
          {currentCourses.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="parent-filter-select"
          aria-label="Filter by status"
        >
          <option value="all">All Statuses</option>
          <option value="not-started">Not Started</option>
          <option value="in-progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="overdue">Overdue</option>
        </select>
        <select
          value={filterPriority}
          onChange={e => setFilterPriority(e.target.value)}
          className="parent-filter-select"
          aria-label="Filter by priority"
        >
          <option value="all">All Priorities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Results count */}
      <p className="parent-assignments__count">{filtered.length} assignments</p>

      {/* Assignment List */}
      <div className="parent-assignments__list">
        {filtered.map(assignment => (
          <div key={assignment.id} className="parent-assignments__item">
            <div className="parent-assignments__item-main">
              <span className="parent-assignments__item-title">{assignment.title}</span>
              <span className="parent-assignments__item-course">{assignment.course}</span>
            </div>
            <div className="parent-assignments__item-details">
              <span className={`parent-assignments__status parent-assignments__status--${assignment.status}`}>
                {formatStatus(assignment.status)}
              </span>
              <span className={`parent-assignments__priority parent-assignments__priority--${assignment.priority}`}>
                {assignment.priority}
              </span>
              <span className="parent-assignments__effort">{assignment.estimatedEffort}</span>
              <span className="parent-assignments__due">{formatDate(assignment.dueDate)}</span>
            </div>
            <div className="parent-assignments__item-indicators">
              {assignment.started && <span className="parent-assignments__started-badge">Started</span>}
              {assignment.studyActivity > 0 && (
                <span className="parent-assignments__activity-badge">{assignment.studyActivity} study sessions</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  const diff = Math.round((date - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 0 && diff <= 7) return `In ${diff} days`;
  if (diff < 0) return `${Math.abs(diff)} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatStatus(status) {
  const labels = { 'not-started': 'Not Started', 'in-progress': 'In Progress', 'completed': 'Completed', 'overdue': 'Overdue', 'submitted': 'Submitted' };
  return labels[status] || status;
}

export default ParentAssignments;
